import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import LanguageSwitcher from "./LanguageSwitcher";
import Logo from "./Logo";

const Header = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const navItems = [
    { label: t("nav.home"), path: "/" },
    { label: t("nav.product"), path: "/producto" },
    { label: t("nav.stablecoin"), path: "/stablecoin" },
    { label: t("nav.aiAgents"), path: "/ai-agents" },
    { label: t("nav.useCases"), path: "/casos-de-uso" },
    { label: t("nav.integration"), path: "/integracion" },
    { label: t("nav.security"), path: "/seguridad" },
    { label: t("nav.contact"), path: "/contacto" }
  ];

  return (
    <header className="header" style={styles.header}>
      <div className="container" style={styles.container}>
        <Logo to="/" variant="light" size="md" />

        {/* Desktop Navigation */}
        <nav className="nav" style={styles.nav}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                ...styles.navLink,
                ...(location.pathname === item.path ? styles.navLinkActive : {})
              }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="rightSide" style={styles.rightSide}>
          <LanguageSwitcher variant="desktop" />
          <Link to="/contacto" className="ctaButton" style={styles.ctaButton} data-testid="header-cta-demo">
            {t("common.requestDemo")}
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="menuButton" style={styles.menuButton}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          data-testid="header-mobile-menu-toggle"
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="mobileMenu" style={styles.mobileMenu}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                ...styles.mobileNavLink,
                ...(location.pathname === item.path ? styles.mobileNavLinkActive : {})
              }}
              onClick={() => setIsMenuOpen(false)}
            >
              {item.label}
            </Link>
          ))}
          <LanguageSwitcher variant="mobile" />
          <Link
            to="/contacto"
            className="mobileCta" style={styles.mobileCta}
            onClick={() => setIsMenuOpen(false)}
            data-testid="header-mobile-cta-demo"
          >
            {t("common.requestDemo")}
          </Link>
        </div>
      )}
    </header>
  );
};

const styles = {
  header: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
    background: "rgba(255, 255, 255, 0.95)",
    backdropFilter: "blur(20px)",
    borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.04)"
  },
  container: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "20px 40px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "20px"
  },
  logo: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    textDecoration: "none",
    color: "#1a202c"
  },
  logoText: {
    fontSize: "24px",
    fontWeight: "700",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text"
  },
  nav: {
    display: "flex",
    gap: "28px",
    alignItems: "center"
  },
  navLink: {
    color: "#4a5568",
    textDecoration: "none",
    fontSize: "15px",
    fontWeight: "500",
    transition: "color 0.2s",
    position: "relative",
    padding: "8px 0"
  },
  navLinkActive: {
    color: "#667eea"
  },
  rightSide: {
    display: "flex",
    alignItems: "center",
    gap: "12px"
  },
  ctaButton: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    padding: "10px 22px",
    borderRadius: "8px",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "600",
    transition: "transform 0.2s, box-shadow 0.2s",
    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)",
    display: "inline-block",
    whiteSpace: "nowrap"
  },
  menuButton: {
    display: "none",
    background: "none",
    border: "none",
    color: "#1a202c",
    cursor: "pointer",
    padding: "8px"
  },
  mobileMenu: {
    display: "none",
    flexDirection: "column",
    padding: "20px",
    background: "rgba(255, 255, 255, 0.98)",
    borderTop: "1px solid rgba(0, 0, 0, 0.08)"
  },
  mobileNavLink: {
    color: "#4a5568",
    textDecoration: "none",
    padding: "12px 0",
    fontSize: "16px",
    borderBottom: "1px solid rgba(0, 0, 0, 0.05)"
  },
  mobileNavLinkActive: {
    color: "#667eea"
  },
  mobileCta: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    padding: "12px 24px",
    borderRadius: "8px",
    textDecoration: "none",
    textAlign: "center",
    marginTop: "16px",
    display: "block"
  }
};

// Responsive: hide desktop nav + right-side on mobile, show menu button + mobile drawer
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @media (max-width: 1024px) {
    header > div:first-child > nav,
    header > div:first-child > div[style*="display: flex"][style*="gap: 12px"] {
      display: none !important;
    }
    header > div:first-child > button {
      display: block !important;
    }
    header > div + div {
      display: flex !important;
    }
  }
`;
document.head.appendChild(styleSheet);

export default Header;
